import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { FormattedMessage } from 'react-intl';
import { v4 as uuidV4 } from 'uuid';
import { Collapse, Container, NavItem, NavLink, TabContent, TabPane, Nav } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

import 'Components/Legal/CookieConsent/CookieConsent.scss';

import FanaticalLogo from 'Components/Icon/FanaticalLogo';
import { setConsent } from 'Redux/ducks/cookie';
import { fetchCookieData } from 'Api/Cookie/cookie-service';
import store from 'Redux/store';

const defaultSettings = {
  necessary: true,
  preferences: true,
  statistics: true,
  marketing: true,
  unclassified: true,
};

const categories = Object.keys(defaultSettings);

export default function CookieConsent({ open, consent, isPolicyPage }) {
  const [settingsOpen, setSettingsOpen] = useState(isPolicyPage);
  const [activeTab, setActiveTab] = useState('1');
  const [activeInnerTab, setActiveInnerTab] = useState('necessary');
  const [checkboxes, setCheckboxes] = useState(isEmpty(consent) ? defaultSettings : consent);
  const [cookieData, setCookieData] = useState(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);

  useEffect(() => {
    if ((settingsOpen || mobileDropdownOpen) && cookieData === null) {
      fetchCookieData().then(setCookieData);
    }
  }, [cookieData, settingsOpen, mobileDropdownOpen]);

  const doConsent = () => {
    const payload = {
      stamp: uuidV4(),
      ver: 1,
      utc: new Date().getTime(),
      ...checkboxes,
    };

    store.dispatch(setConsent(payload));
  };

  const rejectAll = () => {
    const payload = {
      stamp: uuidV4(),
      ver: 1,
      utc: new Date().getTime(),
      necessary: true,
      preferences: false,
      statistics: false,
      marketing: false,
      unclassified: false,
    };

    store.dispatch(setConsent(payload));
  };

  const toggleCheckbox = (name) => {
    setCheckboxes({ ...checkboxes, [name]: !checkboxes[name] });
  };

  const tabLabels = {
    necessary: <FormattedMessage id="cookie.decleration.menu.necessary" defaultMessage="Necessary" />,
    preferences: <FormattedMessage id="cookie.decleration.menu.preferences" defaultMessage="Preferences" />,
    statistics: <FormattedMessage id="cookie.decleration.menu.statistics" defaultMessage="Statistics" />,
    marketing: <FormattedMessage id="cookie.decleration.menu.marketing" defaultMessage="Marketing" />,
    unclassified: <FormattedMessage id="cookie.decleration.menu.unclassified" defaultMessage="Unclassified" />,
  };

  const tabDescriptions = {
    necessary: <FormattedMessage
      id="cookie.decleration.necessary.none1"
      defaultMessage="Necessary cookies are those which are vital to the proper function of the website"
    />,
    preferences: <FormattedMessage
      id="cookie.decleration.preferences.none1"
      defaultMessage="Preference cookies enable a website to remember information that changes the way the website behaves or looks, like your preferred language or the region that you are in."
    />,
    statistics: <FormattedMessage id="cookie.decleration.statistics.none1" defaultMessage="Statistics cookies allow a website to be improved for users based on statistics on how it is used" />,
    marketing: <FormattedMessage id="cookie.decleration.marketing.none1" defaultMessage="Cookies used for marketing purposes" />,
    unclassified: <FormattedMessage id="cookie.decleration.unclassified.none1" defaultMessage="Cookies that do not fit into any of the other categories" />,
  };

  return (
    <section className="wafer-auth-collapsible CookieConsent" role="alertdialog" aria-label="Cookie Consent Policy">
      <Collapse isOpen={open}>
        <Container className="wafer-auth-collapsible-wrapper">
          <h2 className="cookie-main-header h3">
            <FanaticalLogo />
            <FormattedMessage id="cookie.collapsible.header" defaultMessage="This website uses cookies" />
          </h2>
          <p className="cookie-main-intro">
            <FormattedMessage
              id="cookie.background.body"
              defaultMessage="We use cookies to personalise content and ads, to provide social media features and to analyse our traffic. We also share information about your use of our site with our social media, advertising and analytics partners who may combine it with other information that you’ve provided to them or that they’ve collected from your use of their services. You consent to our use of non-essential cookies by choosing 'Accept Cookies' below. In any event, we will continue to use certain cookies and tracking technologies where they are strictly necessary to make this site work."
            />
          </p>
          <div className="accept-settings-btn-container">
            <div className="accept-settings-btn-container-two">
              <button
                className="accept-cookies-btn"
                onClick={() => doConsent()}
              >
                {isEmpty(consent) ? <FormattedMessage id="cookie.button.accept" defaultMessage="Accept All Non-Essential Cookies" /> : <FormattedMessage id="cookie.button.update" defaultMessage="Update Preferences" />}
              </button>

              <button
                className="btn btn-outline-secondary"
                onClick={() => rejectAll()}
              >
                <FormattedMessage id="cookie.button.reject" defaultMessage="Reject All Non-Essential Cookies" />
              </button>
            </div>

            <button
              className="settings-cookies-btn btn btn-link"
              onClick={() => setSettingsOpen(!settingsOpen)}
            >
              <FormattedMessage id="cookie.collapsible.settings" defaultMessage="Settings" />
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
          </div>

          {settingsOpen &&
            <div className="cookie-settings">
              <Nav tabs className="cookie-settings-header-tabs d-none d-md-flex">
                <NavItem>
                  <NavLink
                    className={`${activeTab === '1' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('1'); }}
                  >
                    <FormattedMessage id="cookie.settings.nav.cookie" defaultMessage="Cookie Declaration" />
                  </NavLink>
                </NavItem>

                <NavItem>
                  <NavLink
                    className={`${activeTab === '3' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('3'); }}
                  >
                    <FormattedMessage id="cookie.settings.nav.about" defaultMessage="About Cookies" />
                  </NavLink>
                </NavItem>
              </Nav>

              <div className="CookieConsent__dropdown__mobile">
                <button
                  className="CookieConsent__dropdown__mobile__button"
                  onClick={() => { setMobileDropdownOpen(!mobileDropdownOpen); }}
                >
                  {activeTab === '1' &&
                    <FormattedMessage id="cookie.settings.nav.cookie" defaultMessage="Cookie Declaration" />
                  }
                  {activeTab === '3' &&
                    <FormattedMessage id="cookie.settings.nav.about" defaultMessage="About Cookies" />
                  }
                  <FontAwesomeIcon icon={faChevronDown} />
                </button>
                {mobileDropdownOpen &&
                  <div className="CookieConsent__dropdown__mobile__options">
                    <button
                      className="CookieConsent__dropdown__mobile__options__button"
                      onClick={() => { setActiveTab('1'); setMobileDropdownOpen(!mobileDropdownOpen); }}
                    >
                      <FormattedMessage id="cookie.settings.nav.cookie" defaultMessage="Cookie Declaration" />
                    </button>
                    <button
                      className="CookieConsent__dropdown__mobile__options__button"
                      onClick={() => { setActiveTab('3'); setMobileDropdownOpen(!mobileDropdownOpen); }}
                    >
                      <FormattedMessage id="cookie.settings.nav.about" defaultMessage="About Cookies" />
                    </button>
                  </div>
                }
              </div>

              <div className="cookie-settings-tab-container">
                <TabContent className="cookie-settings-tab-content" activeTab={activeTab}>
                  <TabPane tabId="1" className="cookie-settings-tab-pane">
                    <h5 className="tab-pane-heading">
                      <FormattedMessage id="cookie.decleration.tabheading.cookie-decleration" defaultMessage="Cookie Declaration" />
                    </h5>
                    <div className="cookie-decleration-columns">
                      <div className="left-column">
                        {categories && categories.map(category =>
                          <div
                            key={category}
                            className={`column-tab-container ${activeInnerTab === category ? 'active' : ''}`}
                          >
                            <input
                              disabled={category === 'necessary'}
                              name={category}
                              type="checkbox"
                              defaultChecked={checkboxes[category]}
                              aria-label={category}
                              onClick={() => toggleCheckbox(category)}
                            />
                            <button
                              role="tab"
                              aria-controls={category}
                              aria-selected={activeInnerTab === category}
                              onClick={() => setActiveInnerTab(category)}
                            >
                              {tabLabels[category]}
                            </button>
                          </div>,
                        )}
                      </div>

                      <div className="right-column">
                        {categories && categories.map(category =>
                          <div
                            key={category}
                            className={`tab-pane fade ${activeInnerTab === category ? 'show active' : 'd-none'}`}
                            role="tabpanel"
                            aria-labelledby={category}
                          >
                            <p>
                              {tabDescriptions[category]}
                            </p>
                            {cookieData && cookieData[`cookieTable${category.charAt(0).toUpperCase() + category.slice(1)}`] &&
                              <table className="table">
                                <thead>
                                  <tr>
                                    <th scope="col">
                                      <FormattedMessage id="cookie.decleration.table.header.name" defaultMessage="Name" />
                                    </th>
                                    <th scope="col">
                                      <FormattedMessage id="cookie.decleration.table.header.provider" defaultMessage="Provider" />
                                    </th>
                                    <th scope="col">
                                      <FormattedMessage id="cookie.decleration.table.header.purpose" defaultMessage="Purpose" />
                                    </th>
                                    <th scope="col">
                                      <FormattedMessage id="cookie.decleration.table.header.expiry" defaultMessage="Expiry" />
                                    </th>
                                    <th scope="col">
                                      <FormattedMessage id="cookie.decleration.table.header.type" defaultMessage="Type" />
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {cookieData && cookieData[`cookieTable${category.charAt(0).toUpperCase() + category.slice(1)}`].map(cookie =>
                                    <tr key={`${category}cookies${cookie.Name}`}>
                                      <td className="table-column-name">{cookie.Name}</td>
                                      <td className="table-column-provider">{cookie.Provider}</td>
                                      <td className="table-column-purpose">{cookie.Purpose}</td>
                                      <td className="table-column-expiry">{cookie.Expiry}</td>
                                      <td className="table-column-type">{cookie.Type}</td>
                                    </tr>,
                                  )}
                                </tbody>
                              </table>
                            }
                          </div>,
                        )}
                      </div>
                    </div>
                  </TabPane>

                  <TabPane tabId="3" className="cookie-settings-tab-pane">
                    <h5 className="tab-pane-heading">
                      <FormattedMessage id="cookie.decleration.tabheading.about-cookies" defaultMessage="About Cookies" />
                    </h5>
                    <p>
                      <FormattedMessage id="cookie.about.description1" defaultMessage="Cookies are small text files that can be used by websites to make a user’s experience more efficient." />
                    </p>
                    <p>
                      <FormattedMessage id="cookie.about.description2" defaultMessage="The law states that we can store cookies on your device if they are strictly necessary for the operation of this site. For all other types of cookies we need your permission." />
                    </p>
                    <p>
                      <FormattedMessage id="cookie.about.description3" defaultMessage="This site uses different types of cookies. Some cookies are placed by third party services that appear on our pages." />
                    </p>
                    <p>
                      <FormattedMessage id="cookie.about.description4" defaultMessage="You can at any time change or withdraw your consent from the Cookie Policy." />
                    </p>
                    <p>
                      <FormattedMessage id="cookie.about.description5" defaultMessage="Learn more about who we are, how you can contact us and how we process personal data in our Privacy Policy." />
                    </p>
                  </TabPane>
                </TabContent>
              </div>
            </div>
          }
        </Container>
      </Collapse>
    </section>
  );
}

CookieConsent.propTypes = {
  open: PropTypes.bool,
  isPolicyPage: PropTypes.bool,
  consent: PropTypes.shape({}),
};

CookieConsent.defaultProps = {
  open: false,
  isPolicyPage: false,
  consent: {},
};
